// @ts-strict-ignore
import { isNil } from 'lodash';
import React, { useCallback, useState } from 'react';
import { View } from '@shared/components/View';
import { MBTextInput } from '@shared/components/textInput';
import { MBTextBody1 } from '@shared/components/typography';
import { Colors } from '@shared/styles';
import { WorkflowComponentProps } from './WorkflowComponent';
import { WorkflowComponentWrapper } from './WorkflowComponentWrapper';

type Datum = string | undefined;
type Data = Datum | Datum[];

export function WorkflowTextComponent({
  initialData,
  component,
  onDataChanged,
  isReadOnly,
  showLabel,
  paddingHorizontal,
  iconPad,
  showIcon,
}: WorkflowComponentProps) {
  const textComponent = component as MBAPI.WorkflowTemplate_components_TextComponent;
  const [value, setValue] = useState<string | null>(formatData(initialData));
  const readOnly = isReadOnly || textComponent.readonly;

  const onChange = useCallback(
    (data: string) => {
      setValue(data);
      onDataChanged(textComponent.id, data);
    },
    [onDataChanged, textComponent],
  );

  if (!showLabel && !showIcon) {
    return (
      <WorkflowTextContent
        readOnly={readOnly}
        value={value}
        initialData={initialData}
        onChange={onChange}
        textComponent={textComponent}
      />
    );
  }

  return (
    <WorkflowComponentWrapper
      paddingHorizontal={paddingHorizontal}
      iconPad={iconPad}
      component={textComponent}
      showLabel={showLabel}
      showIcon={showIcon}
    >
      <WorkflowTextContent
        readOnly={readOnly}
        value={value}
        initialData={initialData}
        onChange={onChange}
        textComponent={textComponent}
      />
    </WorkflowComponentWrapper>
  );
}

function WorkflowTextContent({
  readOnly,
  value,
  initialData,
  onChange,
  textComponent,
}: {
  readOnly: boolean;
  value: Data;
  initialData: Data;
  onChange: (data: string) => void;
  textComponent: MBAPI.WorkflowTemplate_components_TextComponent;
}) {
  return (
    <>
      {!readOnly && (
        <View>
          <MBTextInput
            noBorder
            noPadding
            value={formatData(value)}
            onChangeText={onChange}
            placeholder={textComponent.placeholder}
          />
        </View>
      )}
      {readOnly && (
        <View fullWidth>
          <MBTextBody1 color={isEmptyData(initialData) ? Colors.v2.gray50 : Colors.v2.purple}>
            {formatData(initialData) || textComponent.label}
          </MBTextBody1>
        </View>
      )}
    </>
  );
}

function formatData(data: Data): string | null {
  if (Array.isArray(data)) {
    const displayableData = data.filter(isDisplayableDatum);
    return displayableData.length > 0 ? displayableData.join(', ') : null;
  }

  return isDisplayableDatum(data) ? data : null;
}

function isDisplayableDatum(datum: Datum): boolean {
  return !isNil(datum) && datum.length > 0;
}

function isEmptyData(data: Data): boolean {
  if (Array.isArray(data)) {
    return !data.some(isDisplayableDatum);
  }

  return !isDisplayableDatum(data);
}
